import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  IconButton,
  ListItemIcon
} from '@mui/material';
import { EDUGATE_API_URL } from '../../config/serviceConfig';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Lightbulb as LightbulbIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  EmojiObjects as EmojiObjectsIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';

const EvaluationView = ({ projectData, currentUser, onBack }) => {
  const { t } = useTranslation();
  const [evaluationData, setEvaluationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // 安全获取project数据
  const project = projectData?.project || projectData;
  const evaluationType = projectData?.evaluationType || '';

  // 获取评价数据
  const fetchEvaluationData = async () => {
    if (!project?.id) {
      setError(t('yuyan_eval_err_invalid'));
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // 构建API URL，如果有指定评价类型则添加参数
      let apiUrl = `${EDUGATE_API_URL}/api/yuyan/projects/${project.id}/evaluation`;
      if (evaluationType) {
        apiUrl += `?evaluation_type=${evaluationType}`;
      }
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${currentUser.token || ''}`,
          'X-User-ID': currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          // 获取详细评价信息
          const detailResponse = await fetch(`${EDUGATE_API_URL}/api/yuyan/evaluations/${result.data.evaluation_id}`, {
            headers: {
              'Authorization': `Bearer ${currentUser.token || ''}`,
              'X-User-ID': currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username
            }
          });
          
          if (detailResponse.ok) {
            const detailResult = await detailResponse.json();
            if (detailResult.code === 0) {
              setEvaluationData(detailResult.data);
            } else {
              setError(detailResult.message || t('yuyan_eval_err_detail'));
            }
          } else {
            setError(t('yuyan_eval_err_detail') + `: ${detailResponse.status} ${detailResponse.statusText}`);
          }
        } else {
          setError(result.message || t('yuyan_eval_err_fetch'));
        }
      } else if (response.status === 404) {
        // 评价由后台异步生成，未生成时后端会返回 404；这里展示等待提示而不是错误。
        setEvaluationData(null);
      } else {
        setError(t('yuyan_eval_err_fetch') + `: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('获取评价数据错误:', error);
      setError(t('yuyan_eval_err_network', { msg: error.message }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project && project.id) {
      fetchEvaluationData();
    }
  }, [project?.id]);


  // 添加空值检查 - 在所有hooks之后
  if (!projectData) {
    return null;
  }


  // 获取评估结果的颜色
  const getResultColor = (result) => {
    if (result.includes('建议立项') || result.includes('优秀')) return '#4ade80';
    if (result.includes('良好')) return '#38bdf8';
    if (result.includes('待改进')) return '#fbbf24';
    return '#94a3b8';
  };

  // Tab变更处理
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} sx={{ color: '#38bdf8', mb: 2 }} />
        <Typography variant="body2" color="#94a3b8">
          {t('yuyan_eval_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0', background: 'rgba(255, 255, 255, 0.05)' } }}
          >
            {t('yuyan_eval_back')}
          </Button>
        </Box>
        
        <Alert severity="error" sx={{ mb: 3, background: 'rgba(239, 68, 68, 0.1)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          {error}
        </Alert>
        
        <Button
          variant="contained"
          onClick={fetchEvaluationData}
          startIcon={<TrendingUpIcon />}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)'
          }}
        >
          {t('yuyan_eval_reload')}
        </Button>
      </Box>
    );
  }

  if (!evaluationData || !evaluationData.full_detail) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0', background: 'rgba(255, 255, 255, 0.05)' } }}
          >
            {t('yuyan_eval_back')}
          </Button>
        </Box>
        
        <Alert severity="info" sx={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.2)' }}>
          {t('yuyan_eval_no_data')}
        </Alert>
      </Box>
    );
  }

  const { full_detail: fullDetail, radar_data: radarData } = evaluationData;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      {/* 顶部标题栏 */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ color: '#94a3b8', mr: 1, '&:hover': { color: '#e2e8f0' } }}>
          <ArrowBackIcon />
        </IconButton>
        <AssessmentIcon sx={{ mr: 1.5, color: evaluationType === 'conclusion' ? '#4ade80' : '#38bdf8' }} />
        <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
          {evaluationType === 'conclusion' ? t('yuyan_eval_title_conclusion') : t('yuyan_eval_title_proposal')}
        </Typography>
      </Box>

      {/* 提示信息 */}
      <Alert 
        severity="info" 
        icon={<WarningIcon sx={{ color: '#38bdf8' }} />}
        sx={{ 
          mb: 3, 
          background: 'rgba(56, 189, 248, 0.1)', 
          color: '#e2e8f0', 
          border: '1px solid rgba(56, 189, 248, 0.2)',
          '& .MuiAlert-icon': { color: '#38bdf8' }
        }}
      >
        <Typography variant="body2">
          {t('yuyan_eval_important_hint')}
        </Typography>
      </Alert>

      {/* 核心评分卡片 */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '16px' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ color: '#e2e8f0', fontWeight: 600, mb: 1 }}>
                {project.project_name}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap" sx={{ color: '#94a3b8' }}>
                <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                  <PersonIcon fontSize="small" /> {t('yuyan_eval_applicant', { name: project.applicant_name })}
                </Typography>
                <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                  <SchoolIcon fontSize="small" /> {t('yuyan_eval_college', { name: project.college })}
                </Typography>
                <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                  <SpeedIcon fontSize="small" /> {t('yuyan_eval_review_time', { date: new Date(fullDetail.review_time).toLocaleDateString() })}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
              <Box display="flex" flexDirection="column" alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                <Typography variant="h3" sx={{ color: evaluationType === 'conclusion' ? '#4ade80' : '#38bdf8', fontWeight: 700, lineHeight: 1 }}>
                  {fullDetail.total_score}
                  <Typography component="span" variant="h6" sx={{ color: '#64748b', ml: 0.5 }}>/{fullDetail.max_score}</Typography>
                </Typography>
                <Chip 
                  label={fullDetail.evaluation_result} 
                  sx={{ 
                    mt: 1, 
                    bgcolor: `${getResultColor(fullDetail.evaluation_result)}20`, 
                    color: getResultColor(fullDetail.evaluation_result),
                    fontWeight: 600,
                    border: `1px solid ${getResultColor(fullDetail.evaluation_result)}40`
                  }} 
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 详情标签页 */}
      <Paper sx={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '16px', overflow: 'hidden' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ 
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none', fontSize: '1rem', fontWeight: 500 },
            '& .Mui-selected': { color: '#38bdf8' },
            '& .MuiTabs-indicator': { backgroundColor: '#38bdf8' }
          }}
        >
          <Tab label={t('yuyan_eval_tab_overview')} />
          <Tab label={t('yuyan_eval_tab_dimension')} />
          <Tab label={t('yuyan_eval_tab_analysis')} />
          <Tab label={t('yuyan_eval_tab_suggestion')} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* 概览标签页 */}
          {tabValue === 0 && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                <Box sx={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {radarData.dimensions && radarData.dimensions.length > 0 && (
                    <Chart
                      type="radar"
                      width="100%"
                      height={380}
                        options={{
                        chart: {
                          background: 'transparent',
                          toolbar: { show: false },
                          dropShadow: { enabled: false }
                        },
                        theme: { mode: 'dark' },
                        grid: {
                          padding: {
                            top: 10,
                            bottom: 10,
                            left: 40,  // 增加左侧边距
                            right: 40  // 增加右侧边距
                          }
                        },
                        xaxis: {
                          // 将长名称按"与"或"/"拆分为多行数组，实现自动换行
                          categories: radarData.dimensions.map(dim => 
                            dim.name.replace('项目', '').split(/与|\/|和/)
                          ),
                          labels: {
                            show: true,
                            style: {
                              colors: radarData.dimensions.map(() => '#e2e8f0'),
                              fontSize: '11px',
                              fontWeight: 500,
                              fontFamily: 'Inter, sans-serif'
                            },
                            offsetY: 2 // 微调标签垂直位置
                          }
                        },
                        yaxis: { show: false, min: 0, max: 100 },
                        stroke: { width: 2, colors: [evaluationType === 'conclusion' ? '#4ade80' : '#38bdf8'] },
                        fill: { 
                          opacity: 0.2, 
                          colors: [evaluationType === 'conclusion' ? '#4ade80' : '#38bdf8'] 
                        },
                        markers: { 
                          size: 4, 
                          colors: [evaluationType === 'conclusion' ? '#4ade80' : '#38bdf8'], 
                          strokeColors: '#fff', 
                          strokeWidth: 2 
                        },
                        plotOptions: {
                          radar: {
                            size: 100, // 稍微调大一点 (从120->130)
                            offsetX: 0,
                            offsetY: 0,
                            polygons: {
                              strokeColors: 'rgba(255, 255, 255, 0.1)',
                              connectorColors: 'rgba(255, 255, 255, 0.1)',
                              fill: { colors: ['rgba(30, 41, 59, 0.8)', 'rgba(15, 23, 42, 0.8)'] }
                            }
                          }
                        },
                        tooltip: {
                          theme: 'dark',
                          y: { formatter: (val) => `${val}%` }
                        }
                      }}
                      series={[{
                        name: t('yuyan_eval_project_score'),
                        data: radarData.dimensions.map(dim => dim.percentage)
                      }]}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" sx={{ color: '#e2e8f0', mb: 2, fontWeight: 600 }}>{t('yuyan_eval_dimension_score')}</Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  {radarData.dimensions.map((dim, index) => (
                    <Box key={index}>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" sx={{ color: '#cbd5e1' }}>{dim.name}</Typography>
                        <Typography variant="body2" sx={{ color: '#38bdf8', fontWeight: 600 }}>{dim.score}/{dim.max_score}</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={dim.percentage} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          '& .MuiLinearProgress-bar': {
                            background: `linear-gradient(90deg, ${evaluationType === 'conclusion' ? '#22c55e' : '#3b82f6'} 0%, ${evaluationType === 'conclusion' ? '#4ade80' : '#60a5fa'} 100%)`
                          }
                        }} 
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}

          {/* 详细评分标签页 */}
          {tabValue === 1 && (
            <Box>
              {fullDetail.dimension_details.map((dimension, index) => (
                <Accordion 
                  key={index}
                  sx={{ 
                    mb: 2, 
                    background: 'rgba(255, 255, 255, 0.02)', 
                    color: '#e2e8f0',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    border: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}>
                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" pr={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{dimension.name}</Typography>
                      <Typography variant="subtitle1" sx={{ color: '#38bdf8', fontWeight: 600 }}>{dimension.score}/{dimension.max_score}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <TableContainer component={Paper} sx={{ background: 'transparent', boxShadow: 'none', mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{t('yuyan_eval_sub_item')}</TableCell>
                            <TableCell align="right" sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{t('yuyan_eval_score')}</TableCell>
                            <TableCell sx={{ color: '#94a3b8', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{t('yuyan_eval_basis')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dimension.sub_dimensions.map((sub, subIndex) => (
                            <TableRow key={subIndex}>
                              <TableCell sx={{ color: '#cbd5e1', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{sub.name}</TableCell>
                              <TableCell align="right" sx={{ color: '#38bdf8', fontWeight: 500, borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                {sub.score}/{sub.max_score}
                              </TableCell>
                              <TableCell sx={{ color: '#94a3b8', fontSize: '0.875rem', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                {sub.evaluation_basis}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ p: 2, background: 'rgba(56, 189, 248, 0.05)', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.1)' }}>
                      <Typography variant="body2" sx={{ color: '#38bdf8', mb: 0.5, fontWeight: 500 }}>{t('yuyan_eval_dimension_comment')}</Typography>
                      <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.6 }}>{dimension.comment}</Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {/* 优劣分析标签页 */}
          {tabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', background: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#4ade80', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon /> {t('yuyan_eval_advantages')}
                    </Typography>
                    <List dense>
                      {fullDetail.main_advantages.map((item, index) => (
                        <ListItem key={index} alignItems="flex-start">
                          <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80' }} />
                          </ListItemIcon>
                          <ListItemText primary={item} primaryTypographyProps={{ color: '#cbd5e1', lineHeight: 1.6 }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#f87171', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon /> {t('yuyan_eval_disadvantages')}
                    </Typography>
                    <List dense>
                      {fullDetail.main_disadvantages.map((item, index) => (
                        <ListItem key={index} alignItems="flex-start">
                          <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f87171' }} />
                          </ListItemIcon>
                          <ListItemText primary={item} primaryTypographyProps={{ color: '#cbd5e1', lineHeight: 1.6 }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* 改进建议 */}
              <Grid item xs={12}>
                <Card sx={{ background: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#fbbf24', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbIcon /> {t('yuyan_eval_improvement')}
                    </Typography>
                    <Grid container spacing={2}>
                      {fullDetail.improvement_suggestions.map((item, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Box display="flex" gap={1.5}>
                            <Box sx={{ minWidth: 20, height: 20, borderRadius: '50%', bgcolor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, mt: 0.2 }}>
                              {index + 1}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.6 }}>{item}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* 专家建议标签页 */}
          {tabValue === 3 && (
            <Box display="flex" flexDirection="column" gap={3}>
              <Card sx={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#38bdf8', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> {t('yuyan_eval_expert_comment')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#e2e8f0', lineHeight: 1.8, textIndent: '2em' }}>
                    {fullDetail.expert_comment}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#c084fc', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon /> {t('yuyan_eval_student_advice')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#e2e8f0', lineHeight: 1.8 }}>
                    {fullDetail.student_advice}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default EvaluationView;